import type { AppDispatch } from '../../data/store';
import type { WebSocketSessionInfo } from './webSocketService';
import * as tripLegSlice from '../../data/tripleg.slice';
import * as tripLegAdapterSlice from '../../data/slices/trip-leg.slice';
import * as passengerSlice from '../../data/passenger.slice';
import * as providerSlice from '../../data/provider.slice';
import { TripLegEventType } from '../../data/events/trip-leg';

/**
 * Method for handling all TripLeg Events
 * @param sessionInfo The webSocketService sessionInfo object. Contains data copied over from the Redux store as needed
 * @param dispatch The Redux store dispatch function
 * @param detailType A string representing the type of event. Must be one of the TripLegEventType enum values
 * @param detail An object containing the event data, in this case a TripLeg
 */
function updateTripLeg(
  sessionInfo: WebSocketSessionInfo,
  dispatch: AppDispatch,
  detailType: TripLegEventType,
  // biome-ignore lint/suspicious/noExplicitAny: Events are not following a unified type
  detail: any,
) {
  // Entity Adapter supported slice updates
  // When a trip leg is drafted or opened, it's a newly created trip leg, so we
  // have to add it to the store. Some events are special cases that need
  // specific logic, so we divert them to their handlers. The default case is
  // for updating the trip leg from all other events.
  dispatch(tripLegAdapterSlice.addToTripLegEventQueue({ detailType, detail }));
  switch (detailType) {
    case TripLegEventType.TripLegDrafted:
    case TripLegEventType.TripLegOpened:
      dispatch(tripLegAdapterSlice.tripLegOpened(detail));
      break;
    case TripLegEventType.TripLegDriverAssigned:
      dispatch(tripLegAdapterSlice.tripLegDriverAssigned(detail));
      break;
    case TripLegEventType.TripLegExceptionCleared:
      dispatch(tripLegAdapterSlice.tripLegExceptionCleared(detail));
      break;
    default:
      dispatch(tripLegAdapterSlice.tripLegUpdated(detail));
  }

  // TODO: Remove the below switch case once all components that use the old
  // trip leg slice have been migrated to the entity adapter supported slice.
  switch (detailType) {
    case TripLegEventType.TripLegDrafted:
      dispatch(tripLegSlice.setTripLegEventEmitted({ detailType, detail }));
      break;
    case TripLegEventType.TripLegOpened:
      dispatch(tripLegSlice.setTripLegEventEmitted({ detailType, detail }));
      dispatch(
        providerSlice.completePendingReassignTripLegAsync(
          detail.tripLegReferenceID,
        ),
      );
      break;
    case TripLegEventType.TripLegException:
    case TripLegEventType.TripLegExceptionAssigned:
    case TripLegEventType.TripLegExceptionCleared:
    case TripLegEventType.TripLegEscalationCleared:
      dispatch(tripLegSlice.setTripLegEventEmitted({ detailType, detail }));
      break;
    case TripLegEventType.TripLegInventoried:
    case TripLegEventType.DraftTripLegUpdated:
    case TripLegEventType.OpenTripLegUpdated:
    case TripLegEventType.TripLegOffered:
    case TripLegEventType.OfferedTripLegUpdated:
    case TripLegEventType.TripLegManuallyAllocated:
    case TripLegEventType.TripLegAccepted:
    case TripLegEventType.AcceptedTripLegUpdated:
    case TripLegEventType.TripLegConfirmed:
    case TripLegEventType.TripLegDriverInTransit:
    case TripLegEventType.TripLegBoarded:
    case TripLegEventType.TripLegArrived:
    case TripLegEventType.TripLegCompleted:
    case TripLegEventType.TripLegRejected:
    case TripLegEventType.TripLegCanceled:
      dispatch(
        passengerSlice.updateTripLegInReservation({
          reservationReferenceID: detail.reservationReferenceID,
          tripLegReferenceID: detail.tripLegReferenceID,
          transportationProviderID: detail.transportationProviderID,
          status: detail.status,
        }),
      );
      dispatch(tripLegSlice.setTripLegEventEmitted({ detailType, detail }));
      break;
    case TripLegEventType.TripLegCanceledLate:
    case TripLegEventType.TripLegNotLoaded:
    case TripLegEventType.TripLegAbandoned:
    case TripLegEventType.TripLegDenied:
    case TripLegEventType.TripLegHeld:
    case TripLegEventType.OnHoldTripLegUpdated:
    case TripLegEventType.TripLegUnderDiscussion:
    case TripLegEventType.TripLegUnderDiscussionCleared:
      dispatch(
        passengerSlice.updateTripLegInReservation({
          reservationReferenceID: detail.reservationReferenceID,
          tripLegReferenceID: detail.tripLegReferenceID,
          transportationProviderID: detail.transportationProviderID,
          status: detail.status,
        }),
      );
      dispatch(tripLegSlice.setTripLegEventEmitted({ detailType, detail }));
      break;
    case TripLegEventType.TripLegDriverAssigned:
      dispatch(tripLegSlice.upsertAllTripLegsDriverAssigned(detail));
      dispatch(tripLegSlice.setTripLegEventEmitted({ detailType, detail }));
      break;
    case TripLegEventType.TripLegEscalated:
      dispatch(tripLegSlice.handleTripLegEscalated(detail.tripLegReferenceID));
      dispatch(tripLegSlice.setTripLegEventEmitted({ detailType, detail }));
      break;
    case TripLegEventType.TripLegCountsUpdated:
      dispatch(tripLegSlice.setTripLegCounts(detail));
      break;
  }
}

export { TripLegEventType, updateTripLeg };
